import { useEffect, useState, useCallback } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiX } from 'react-icons/fi';
import {
  getSectionItems, createSectionItem, updateSectionItem, deleteSectionItem,
  getTestimonials, createTestimonial, updateTestimonial, deleteTestimonial,
} from '../../api';
import { useToast } from '../../components/Toast';
import Loader from '../../components/Loader';

const TABS = [
  { key: 'features', label: 'Highlights', hint: 'Cards below the banner', icon: true },
  { key: 'process', label: 'How it works', hint: 'Numbered steps', icon: false },
  { key: 'industries', label: 'Who we serve', hint: 'Industry cards', icon: true },
  { key: 'why', label: 'Why us points', hint: 'Checklist points', icon: false },
  { key: 'testimonials', label: 'Testimonials', hint: 'Customer quotes', testimonial: true },
];

const emptyItem = { icon: '⭐', title: '', subtitle: '', order: 0, active: true };
const emptyTesti = { name: '', role: '', quote: '', rating: 5, order: 0, active: true };

export default function AdminHomeContent() {
  const [tab, setTab] = useState(TABS[0]);
  const [items, setItems] = useState(null);
  const [form, setForm] = useState(emptyItem);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const { toast, ToastHost } = useToast();

  const load = useCallback(() => {
    setItems(null);
    const p = tab.testimonial ? getTestimonials() : getSectionItems(tab.key);
    p.then(setItems).catch(() => setItems([]));
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const reset = () => { setForm(tab.testimonial ? emptyTesti : emptyItem); setEditId(null); };
  // Reset the form whenever the active tab changes.
  useEffect(() => {
    setForm(tab.testimonial ? emptyTesti : emptyItem);
    setEditId(null);
  }, [tab]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (tab.testimonial) {
        if (!form.name.trim() || !form.quote.trim()) { setSaving(false); return toast('Name and quote are required', 'error'); }
        const payload = { ...form, rating: Number(form.rating) || 5, order: Number(form.order) || 0 };
        if (editId) await updateTestimonial(editId, payload);
        else await createTestimonial(payload);
      } else {
        if (!form.title.trim()) { setSaving(false); return toast('Title is required', 'error'); }
        const payload = { ...form, section: tab.key, order: Number(form.order) || 0 };
        if (editId) await updateSectionItem(editId, payload);
        else await createSectionItem(payload);
      }
      toast('Saved');
      reset();
      load();
    } catch (err) {
      toast('Save failed', 'error');
    } finally { setSaving(false); }
  };

  const edit = (it) => {
    setEditId(it._id);
    setForm(tab.testimonial
      ? { name: it.name, role: it.role || '', quote: it.quote, rating: it.rating || 5, order: it.order || 0, active: it.active !== false }
      : { icon: it.icon || '⭐', title: it.title, subtitle: it.subtitle || '', order: it.order || 0, active: it.active !== false });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const remove = async (it) => {
    if (!window.confirm('Delete this item?')) return;
    if (tab.testimonial) await deleteTestimonial(it._id);
    else await deleteSectionItem(it._id);
    toast('Deleted');
    load();
  };

  const toggleActive = async (it) => {
    const patch = { active: !it.active };
    if (tab.testimonial) await updateTestimonial(it._id, patch);
    else await updateSectionItem(it._id, patch);
    load();
  };

  return (
    <div>
      <div className="acontent__head">
        <div>
          <h1>Home Content</h1>
          <p className="muted" style={{ fontSize: 14 }}>Manage the editable sections on your home page. (Headings are in Settings → Home sections.)</p>
        </div>
      </div>

      <div className="toolbar">
        {TABS.map((t) => (
          <button key={t.key} className={`chip ${tab.key === t.key ? 'active' : ''}`} onClick={() => setTab(t)}>{t.label}</button>
        ))}
      </div>

      <form className="acard" onSubmit={save}>
        <div className="between" style={{ marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>{editId ? 'Edit' : <><FiPlus /> Add</>} — {tab.label} <span className="muted" style={{ fontWeight: 400, fontSize: 13 }}>({tab.hint})</span></h3>
          {editId && <button type="button" className="btn btn-ghost btn-sm" onClick={reset}><FiX /> Cancel</button>}
        </div>

        {tab.testimonial ? (
          <div className="form-grid">
            <div className="field"><label>Name <span className="req">*</span></label><input className="input" value={form.name} onChange={(e) => set('name', e.target.value)} /></div>
            <div className="field"><label>Role / Company</label><input className="input" value={form.role} onChange={(e) => set('role', e.target.value)} placeholder="Fuel Station Owner, Kochi" /></div>
            <div className="field full"><label>Quote <span className="req">*</span></label><textarea className="textarea" value={form.quote} onChange={(e) => set('quote', e.target.value)} /></div>
            <div className="field"><label>Rating (1–5)</label><input className="input" type="number" min="1" max="5" value={form.rating} onChange={(e) => set('rating', e.target.value)} /></div>
            <div className="field"><label>Order</label><input className="input" type="number" value={form.order} onChange={(e) => set('order', e.target.value)} /></div>
          </div>
        ) : (
          <div className="form-grid">
            {tab.icon && <div className="field"><label>Icon (emoji)</label><input className="input" value={form.icon} onChange={(e) => set('icon', e.target.value)} placeholder="⚡" /></div>}
            <div className="field"><label>Title <span className="req">*</span></label><input className="input" value={form.title} onChange={(e) => set('title', e.target.value)} /></div>
            <div className="field full"><label>{tab.key === 'process' || tab.key === 'why' ? 'Description' : 'Subtitle'}</label><input className="input" value={form.subtitle} onChange={(e) => set('subtitle', e.target.value)} /></div>
            <div className="field"><label>Order</label><input className="input" type="number" value={form.order} onChange={(e) => set('order', e.target.value)} /></div>
          </div>
        )}
        <button className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : editId ? 'Update' : 'Add'}</button>
      </form>

      {items === null ? <Loader /> : (
        <div className="table-wrap">
          <table className="atable">
            <thead><tr><th>Order</th>{tab.testimonial ? <><th>Name</th><th>Quote</th><th>Rating</th></> : <><th>Item</th><th>Detail</th></>}<th>Active</th><th></th></tr></thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan={6} className="muted" style={{ textAlign: 'center', padding: 30 }}>No items yet — add one above.</td></tr>}
              {items.map((it) => (
                <tr key={it._id}>
                  <td>{it.order}</td>
                  {tab.testimonial ? (
                    <>
                      <td>{it.name}<div className="muted" style={{ fontSize: 12 }}>{it.role}</div></td>
                      <td style={{ maxWidth: 340 }}>{it.quote}</td>
                      <td>{'★'.repeat(it.rating || 5)}</td>
                    </>
                  ) : (
                    <>
                      <td>{tab.icon && <span style={{ fontSize: 20, marginRight: 8 }}>{it.icon}</span>}{it.title}</td>
                      <td className="muted" style={{ maxWidth: 360 }}>{it.subtitle}</td>
                    </>
                  )}
                  <td><button className={`toggle ${it.active ? 'on' : ''}`} onClick={() => toggleActive(it)} /></td>
                  <td><div className="row-actions">
                    <button className="icon-btn" onClick={() => edit(it)}><FiEdit2 /></button>
                    <button className="icon-btn danger" onClick={() => remove(it)}><FiTrash2 /></button>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <ToastHost />
    </div>
  );
}
